import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import { RestError } from '@dabble/rest-client';
import { useUserStore } from '@/stores/user';
import { defineStore } from 'pinia';
import posthog from 'posthog-js';
import { computed, readonly, ref } from 'vue';

export interface AuthAccount {
  uid: string;
  name?: string;
  email?: string;
  photoUrl?: string;
  admin?: boolean;
  lastSeen?: number;
}

/**
 * Holds the list of accounts the browser is currently signed in to (the
 * sealed `dbl2` cookie maintained by dabble-rest), and the currently-active
 * uid. Mirrors the pattern in dabble-writer-3.0's accounts store, stripped of
 * the hub/spoke worker layer.
 *
 * Active-uid resolution priority on init:
 *   1. sessionStorage.impersonate  (admin acting as a user)
 *   2. sessionStorage.uid
 *   3. localStorage.uid
 *   4. first account from GET /auth/accounts
 */
export const useAccountsStore = defineStore('accounts', () => {
  const accounts = ref<AuthAccount[]>([]);
  const currentUid = ref<string>('');
  const isImpersonating = ref(false);
  const userDataReady = ref(false);
  let switchInFlight: Promise<void> | null = null;
  let initPromise: Promise<void> | null = null;

  const otherAccounts = computed(() => accounts.value.filter((a) => a.uid !== currentUid.value));

  function init(): Promise<void> {
    if (initPromise) return initPromise;
    initPromise = (async () => {
      try {
        migrateLegacyToken();
        const userStore = useUserStore();

        const impersonate = sessionStorage.getItem('impersonate') || '';
        const uid =
          impersonate ||
          sessionStorage.getItem('uid') ||
          localStorage.getItem('uid') ||
          (await loadDefaultUidFromRest());

        if (!uid) return;

        isImpersonating.value = !!impersonate;
        currentUid.value = uid;
        sessionStorage.setItem('uid', uid);
        // Impersonation is session-only — never write the impersonated uid to
        // localStorage, otherwise a tab close would leave the admin "stuck" as
        // the target user on next visit.
        if (!impersonate) localStorage.setItem('uid', uid);

        try {
          await userStore.fetchUser(uid);
        } catch (err) {
          // A 401 means this uid has no valid session — the `dbl2` cookie has
          // expired/been cleared, or the user signed out elsewhere — even though
          // a stale uid marker survived in storage. That's an expected
          // unauthenticated state during bootstrap, not a reportable error
          // (DABBLE-ACCOUNTS-2-6). Drop the stale markers so the app settles into
          // a clean logged-out state and we don't re-fire the same 401 on every
          // reload. Mirrors the sibling loadAccounts() 401 swallow (#92). Any
          // other failure — including a non-401 RestError — is still reported.
          if (err instanceof RestError && err.code === 401) {
            clearActiveUid();
            return;
          }
          logError('Failed to load user:', err);
        }

        // Best-effort populate the list for the Switch Account dropdown.
        loadAccounts().catch(() => {});
      } finally {
        userDataReady.value = true;
      }
    })();
    return initPromise;
  }

  async function loadAccounts() {
    try {
      const list = (await rest.get('/auth/accounts').credentials('include').send()) as AuthAccount[] | null;
      accounts.value = list ?? [];
      return accounts.value;
    } catch (err) {
      // A 401 here is expected when the browser has no valid session yet (e.g. on
      // the login page before sign-in, or after the session cookie expires).
      // loadAccounts is best-effort — see the `loadAccounts().catch(() => {})`
      // call site — so swallow expected auth failures instead of reporting them
      // to Sentry as errors (DABBLE-ACCOUNTS-2-B). Mirrors loadDefaultUidFromRest,
      // which already treats the same call's failure as benign.
      if (!(err instanceof RestError && err.code === 401)) {
        logError('Failed to load accounts:', err);
      }
      return [];
    }
  }

  async function loadDefaultUidFromRest(): Promise<string> {
    try {
      const list = (await rest.get('/auth/accounts').credentials('include').send()) as AuthAccount[] | null;
      return list?.[0]?.uid || '';
    } catch {
      return '';
    }
  }

  /**
   * Drop every marker that points at an active account, returning the store to
   * a clean logged-out state. Used when bootstrap discovers a stale uid whose
   * session is no longer valid (a 401 from `/auth/user`). Mirrors the cleanup
   * performed by the sign-out / switch flows.
   */
  function clearActiveUid() {
    currentUid.value = '';
    isImpersonating.value = false;
    sessionStorage.removeItem('impersonate');
    sessionStorage.removeItem('uid');
    localStorage.removeItem('uid');
  }

  /**
   * Switch the active account to another already-signed-in user.
   *
   * Purely client-side: write the new uid to `sessionStorage.uid` (this tab) and
   * `localStorage.uid` (default for new tabs) and reload. Init re-derives the
   * active uid from those markers, and requests carry it as `x-uid`. The shared
   * `dbl2` cookie is never reordered, so a second tab keeps its own account.
   */
  async function switchAccount(uid: string) {
    if (!uid || uid === currentUid.value) return;
    // Re-entrancy guard — set synchronously before any await.
    if (switchInFlight) return switchInFlight;

    switchInFlight = (async () => {
      sessionStorage.removeItem('impersonate');
      sessionStorage.setItem('uid', uid);
      localStorage.setItem('uid', uid);
      posthog.reset();
      location.assign('/');
    })();

    try {
      await switchInFlight;
    } finally {
      switchInFlight = null;
    }
  }

  function stopImpersonating() {
    sessionStorage.removeItem('impersonate');
    posthog.reset();
    location.assign('/');
  }

  /**
   * Stop impersonating and bounce back to the admin app on the matching
   * environment (local / test / prod), derived by swapping the `accounts.`
   * subdomain for `admin.` in the current hostname.
   */
  function stopImpersonatingToAdmin() {
    sessionStorage.removeItem('impersonate');
    posthog.reset();
    const adminHost = location.hostname.replace(/^accounts\./, 'admin.');
    location.assign(`${location.protocol}//${adminHost}/`);
  }

  /**
   * One-time migration from the legacy `userToken` JSON envelope to the
   * plain `localStorage.uid` scheme. Runs at the top of init() so existing
   * users stay signed in across the upgrade.
   */
  function migrateLegacyToken() {
    if (localStorage.getItem('uid')) return;
    const legacy = localStorage.getItem('userToken');
    if (!legacy) return;
    try {
      const parsed = JSON.parse(legacy);
      if (parsed?.value && (!parsed.expirey || Date.parse(parsed.expirey) > Date.now())) {
        localStorage.setItem('uid', parsed.value);
      }
    } catch {}
    localStorage.removeItem('userToken');
  }

  return {
    accounts: readonly(accounts),
    otherAccounts,
    currentUid: readonly(currentUid),
    isImpersonating: readonly(isImpersonating),
    userDataReady: readonly(userDataReady),
    init,
    loadAccounts,
    switchAccount,
    stopImpersonating,
    stopImpersonatingToAdmin,
  };
});
